import * as impl from './impl.ts';
import { authenticateSecret, Authenticator } from './core.ts';
import { randomId } from '../common/ids.ts';
import { sign, unsign } from '../common/jwt.ts';
import type { Authentication, AuthenticationRequest, AuthenticationResponse } from './types.ts';
import { exceptionInfo, exceptionMessage } from '../common/types.ts';
import { getLogger } from '../logger.ts';

const log = getLogger('gateway.auth.basic');
async function authenticate({authentication}: AuthenticationRequest,
                            secret: string,
                            ttl: number,
                            secretVerifier?: (username?: string, password?: string) => Promise<boolean>): Promise<AuthenticationResponse> {
    switch (authentication?.method) {
        case 'secret': {
            if (secretVerifier) {
                if (!(await secretVerifier(authentication.login, authentication.secret))) {
                    throw exceptionInfo("Invalid login/secret", {type: 'failure', message: 'Invalid login/secret'});
                }
            }
            const response = await authenticateSecret(authentication);
            const accessToken = sign({
                user: response.user,
                exp: Math.floor((Date.now() + ttl) / 1000 )
            }, secret);
            return {...response, accessToken}

        }
        case 'access-token': {
            try {
                const token = (authentication as Required<Pick<Authentication, 'token'>>).token;
                const user = (unsign(token, secret)).user as string | undefined;
                // also consider now/ttl
                return {type: 'success', login: user, user, accessToken: token};
            } catch (e) {
                throw exceptionInfo(`Invalid or expired token:${exceptionMessage(e)}`, {type: 'failure', message: 'Invalid or expired token: ' + exceptionMessage(e)}, e);
            }
        }
        default: {
            const msg = `Unknown authentication method '${authentication?.method}'`;
            log.debug(msg);
            throw exceptionInfo(msg, {type: 'failure', message: msg});
        }
    }
}

class BasicAuthenticator implements impl.AuthImpl {
    readonly #secret: string;
    readonly #ttl: number;
    readonly #secretVerifier?: (username?: string, password?: string) => Promise<boolean>;

    constructor(
        secret: string,
        ttl: number,
        secretVerifier?: (username?: string, password?: string) => Promise<boolean>) {
        this.#secret = secret;
        this.#ttl = ttl;
        this.#secretVerifier = secretVerifier;
    }

    auth(authenticationRequest: AuthenticationRequest) {
        return authenticate(authenticationRequest, this.#secret, this.#ttl, this.#secretVerifier);
    }
}

export interface BasicAuthenticatorConfig extends impl.DefaultAuthenticatorConfig {
    ttl?: number,
    secretVerifier?: (login?: string, secret?: string) => Promise<boolean>
}

export function authenticator(config: BasicAuthenticatorConfig): Authenticator {
    const ttl = 1000 * (config.ttl ?? 60000);
    const secretVerifier = config.secretVerifier;
    if (secretVerifier) {
        log.debug('will use secret verifier');
    }
    return impl.authenticator(config, new BasicAuthenticator(randomId(), ttl, secretVerifier))
}
